import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, ArrowRight, Sparkle, Lightning, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { free: Sparkle, pro: Lightning, agency: Buildings };

const FEATURES = {
    free: [
        "Up to 3 active cases",
        "AI Negotiation Coach (10 prompts/mo)",
        "Manual judgment-interest calculator",
        "Public pay-for-deletion offer pages",
    ],
    pro: [
        "Unlimited active cases",
        "Unlimited AI generations (Claude Sonnet 4.5)",
        "Auto-fetch RECAP documents + Gemini OCR",
        "PDF export with e-signature",
        "Resend-powered offer emails",
        "Counter Offers intelligence dashboard",
    ],
    agency: [
        "Everything in Pro",
        "Unlimited clients & shared workspaces",
        "Multi-seat collaboration",
        "Priority CourtListener fetch queue",
        "Custom branded offer pages",
        "Dedicated success manager",
    ],
};

const TIER_ORDER = ["free", "pro", "agency"];

export default function Billing() {
    const { user, refreshUser } = useAuth();
    const nav = useNavigate();
    const [params, setParams] = useSearchParams();
    const [tiers, setTiers] = useState({});
    const [loadingTier, setLoadingTier] = useState(null);
    const [polling, setPolling] = useState(false);
    const pollTimer = useRef(null);
    const pollAlive = useRef(true);

    const currentTier = user?.plan_tier || "free";

    useEffect(() => {
        api.get("/billing/tiers")
            .then((r) => setTiers(r.data))
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    // Polling on return from Stripe checkout
    useEffect(() => {
        const sessionId = params.get("session_id");
        const cancelled = params.get("cancelled");
        if (cancelled) {
            toast.info("Checkout cancelled. No charge made.");
            setParams({}, { replace: true });
            return;
        }
        if (!sessionId) return;
        let attempts = 0;
        const maxAttempts = 8;
        pollAlive.current = true;
        setPolling(true);
        const tick = async () => {
            if (!pollAlive.current) return;
            attempts++;
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                if (!pollAlive.current) return;
                if (data.payment_status === "paid") {
                    toast.success(`You're now on the ${tiers[data.tier]?.label || data.tier} plan.`);
                    setPolling(false);
                    setParams({}, { replace: true });
                    if (refreshUser) await refreshUser();
                    return;
                }
                if (data.status === "expired") {
                    toast.error("Checkout session expired.");
                    setPolling(false);
                    setParams({}, { replace: true });
                    return;
                }
                if (attempts < maxAttempts) {
                    pollTimer.current = setTimeout(tick, 2000);
                } else {
                    toast.info("Still waiting on Stripe — refresh in a minute if your plan hasn't updated.");
                    setPolling(false);
                }
            } catch (e) {
                if (!pollAlive.current) return;
                toast.error(formatApiError(e));
                setPolling(false);
            }
        };
        tick();
        return () => {
            pollAlive.current = false;
            if (pollTimer.current) clearTimeout(pollTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, tiers]);

    const upgrade = async (tier) => {
        setLoadingTier(tier);
        try {
            const origin = window.location.origin;
            const { data } = await api.post("/billing/checkout", {
                tier,
                origin_url: origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e));
            setLoadingTier(null);
        }
    };

    return (
        <div className="space-y-10" data-testid="billing-page">
            <div>
                <p className="overline">Billing</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Pick a plan that fits your caseload</h1>
                <p className="text-slate-600 mt-3 max-w-2xl">
                    Every plan includes core judgment dispute & negotiation tools. Upgrade for unlimited
                    AI, RECAP auto-fetch, multi-client agency features, and Counter Offers intelligence.
                </p>
                <p className="text-xs text-slate-500 mt-3 italic">
                    Subscription billed monthly in USD via Stripe. Cancel any time.
                </p>
            </div>

            {polling && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="billing-polling">
                    Confirming payment with Stripe…
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {TIER_ORDER.map((key) => {
                    const tier = tiers[key];
                    const isFree = key === "free";
                    const Icon = ICONS[key] || Sparkle;
                    const isCurrent = currentTier === key || (isFree && !currentTier);
                    const highlight = key === "pro";
                    return (
                        <Card
                            key={key}
                            data-testid={`tier-card-${key}`}
                            className={`p-6 flex flex-col ${
                                highlight
                                    ? "border-[#0F1E3D] shadow-lg ring-1 ring-[#0F1E3D]/20"
                                    : "border-slate-200"
                            }`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon size={22} weight="duotone" className="text-[#0F1E3D]" />
                                    <h2 className="font-serif text-2xl">{isFree ? "Free" : (tier?.label || key)}</h2>
                                </div>
                                {highlight && (
                                    <span className="text-[10px] uppercase tracking-wider bg-[#0F1E3D] text-white px-2 py-0.5 rounded">
                                        Most popular
                                    </span>
                                )}
                            </div>
                            <div className="mt-4 flex items-baseline gap-1.5">
                                <span className="font-mono text-4xl font-semibold text-slate-900">
                                    {isFree ? "$0" : tier ? `$${tier.amount.toFixed(0)}` : "—"}
                                </span>
                                <span className="text-sm text-slate-500">
                                    /{tier?.interval || "month"}
                                </span>
                            </div>
                            <ul className="mt-5 space-y-2.5 text-sm flex-1">
                                {FEATURES[key].map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-slate-700">
                                        <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6">
                                {isCurrent ? (
                                    <Button disabled variant="outline" className="w-full h-11" data-testid={`tier-current-${key}`}>
                                        Current plan
                                    </Button>
                                ) : isFree ? (
                                    <Button
                                        variant="outline"
                                        className="w-full h-11"
                                        onClick={() => nav("/dashboard")}
                                        data-testid={`tier-back-${key}`}
                                    >
                                        Continue on free
                                    </Button>
                                ) : (
                                    <Button
                                        className={`w-full h-11 ${highlight ? "bg-[#0F1E3D] hover:bg-[#1A2C57]" : ""}`}
                                        onClick={() => upgrade(key)}
                                        disabled={loadingTier === key}
                                        data-testid={`tier-upgrade-${key}`}
                                    >
                                        {loadingTier === key ? "Redirecting…" : (
                                            <>Upgrade to {tier?.label || key} <ArrowRight size={14} className="ml-1.5" /></>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <p className="text-xs text-slate-500 max-w-2xl">
                LienLift Direct is a judgment information & document service — not a law firm. Subscriptions cover
                platform usage; they do not include legal representation.
            </p>
        </div>
    );
}
